import { useState, type FunctionComponent } from 'react'
import { Modal } from '@retailer-platform/shared-components'
import { errors } from '../../../../utils/error-handling/errors'
import { useRequestBreakGlassPartnerWriteAccess } from '../../../../api/current-account/useRequestBreakGlassPartnerWriteAccess.hooks'
import { RequestAccessPage } from './RequestAccessPage'
import { ConfirmationPage } from './ConfirmationPage'

export interface BreakGlassModalProps {
  isOpen: boolean
  onClose: () => void
  partnerIds: number[]
  partnerNames: string[]
}

const CONFIRMATION_SCREEN_DURATION_MS = 7000

export const BreakGlassModal: FunctionComponent<BreakGlassModalProps> = ({
  isOpen,
  onClose,
  partnerIds,
  partnerNames,
}) => {
  const [reason, setReason] = useState('')
  const [ticketUrl, setTicketUrl] = useState('')
  const [error, setError] = useState(null)
  const [showSuccessScreen, setShowSuccessScreen] = useState(false)

  const { submit, apiLoading, apiError } = useRequestBreakGlassPartnerWriteAccess()

  const handleRequestAccess = async () => {
    setError(null)
    try {
      const result = await submit({
        partnerIds: partnerIds.map(String),
        userJustification: reason,
        ticketUrl: ticketUrl,
      })
      if (result?.data?.enterpriseAccountRequestBreakGlassPartnerWriteAccess?.success) {
        setShowSuccessScreen(true)

        setTimeout(() => {
          onClose()
        }, CONFIRMATION_SCREEN_DURATION_MS)
      }
    } catch (error) {
      // Report the error to Sentry
      // We'll also show a generic error message in the request form when error is set
      errors.captureException(error, 'Failed to request break glass access')
      setError(error)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      maxHeight={800}
      maxWidth={628}
      hideCloseButton
      shouldReturnFocusAfterClose
      styleOverrides={{
        content: {
          borderRadius: 0,
          overflowY: 'auto',
        },
      }}
    >
      {showSuccessScreen ? (
        <ConfirmationPage partnerNames={partnerNames} onClose={onClose} />
      ) : (
        <RequestAccessPage
          reason={reason}
          ticketUrl={ticketUrl}
          onTicketUrlChange={setTicketUrl}
          onReasonChange={setReason}
          onCancel={onClose}
          onRequestAccess={handleRequestAccess}
          onClose={onClose}
          partnerNames={partnerNames}
          isLoading={apiLoading}
          error={apiError || error}
        />
      )}
    </Modal>
  )
}
